import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiHeart, FiSun, FiStar } = FiIcons;

const concepts = [
  {
    icon: FiHeart,
    title: '信任',
    subtitle: 'Trust',
    japanese: '信頼',
    description: '將選擇權交給廚師，是對其技藝與判斷的最高敬意。這份信任建立了獨特的用餐關係。',
    gradient: 'from-rose-500 to-pink-500'
  },
  {
    icon: FiSun,
    title: '季節',
    subtitle: 'Season',
    japanese: '季節',
    description: '順應自然時序，選用當季最美味的食材。每個季節都有其獨特的風味與故事。',
    gradient: 'from-amber-500 to-orange-500'
  },
  {
    icon: FiStar,
    title: '極致',
    subtitle: 'Excellence',
    japanese: '極致',
    description: '從食材到技法，從擺盤到服務，每個細節都追求完美。這是匠人精神的具體展現。',
    gradient: 'from-blue-500 to-purple-500'
  }
];

function ConceptSection() {
  return (
    <section id="concept" className="py-24 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 dark:opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="container-custom relative z-10">
        {/* 🚀 優化：減少動畫時間和 viewport margin */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            Omakase 的核心精神是什麼？
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            三個關鍵概念，構成了 Omakase 獨特的文化底蘊
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {concepts.map((concept, index) => (
            <motion.div
              key={concept.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              className="group relative"
            >
              <div className="card p-8 h-full">
                {/* Icon */}
                <div className="mb-6">
                  <motion.div
                    className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${concept.gradient} p-4 shadow-lg`}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <SafeIcon icon={concept.icon} className="w-full h-full text-white" />
                  </motion.div>
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                  {concept.title}
                </h3>
                <p className={`text-sm font-medium bg-gradient-to-r ${concept.gradient} bg-clip-text text-transparent mb-1`}>
                  {concept.subtitle}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                  {concept.japanese}
                </p>

                {/* Description */}
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                  {concept.description}
                </p>

                {/* Hover Effect */}
                <motion.div
                  className={`absolute inset-0 rounded-xl bg-gradient-to-br ${concept.gradient} opacity-0 group-hover:opacity-5 transition-opacity -z-10`}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default ConceptSection;